import type { GatewayError, Module } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";

import type { Permission } from "~/types/auth.type";

/** Equipment label shown next to the module address in the error list. */
type NamedEquipment = { name: string };

interface LiveErrorEquipment {
  boilerRoom?: NamedEquipment;
  airHandlerUnit?: NamedEquipment;
}

/**
 * Variable-data grants for the equipment this dialog was opened on — never mix the two families.
 * A module whose init failed is linked to no equipment yet; either family's grant then applies so it can still be reset.
 */
export const getLiveErrorVariableDataPermissions = (
  permissions: Permission,
  { boilerRoom, airHandlerUnit }: LiveErrorEquipment,
) => {
  const boilerRoomGrants = permissions.module?.boilerroom?.variableData;
  const airHandlerUnitGrants = permissions.module?.airHandlerUnit?.variableData;
  if (boilerRoom) return boilerRoomGrants;
  if (airHandlerUnit) return airHandlerUnitGrants;
  return {
    read: boilerRoomGrants?.read || airHandlerUnitGrants?.read,
    update: boilerRoomGrants?.update || airHandlerUnitGrants?.update,
  };
};

export interface ErrorMessageFormated {
  date: string;
  errors: {
    name: string;
    ip: string;
    messages: { msg: string; type: string };
  }[];
}

interface FormatErrorProps {
  errors: GatewayError[];
  module: Module;
  airHandlerUnit?: NamedEquipment;
  boilerRoom?: NamedEquipment;
}

export const formatError = ({ errors, module, airHandlerUnit, boilerRoom }: FormatErrorProps): ErrorMessageFormated => {
  const errorMessage: ErrorMessageFormated = {
    date: dayjs().format("L - LTS"),
    errors: [],
  };

  errors.forEach(error => {
    const name = airHandlerUnit?.name ?? boilerRoom?.name ?? "";

    errorMessage.errors.push({
      name,
      ip: module.url ?? module.bacnetDeviceId?.toString() ?? "",
      messages: { msg: error.msg ?? "", type: error.type ?? "" },
    });
  });

  return errorMessage;
};

/** Skip the initial empty snapshot; record clears only after errors were seen. */
export const shouldAppendLiveErrorSnapshot = (
  previous: ErrorMessageFormated["errors"] | undefined,
  current: ErrorMessageFormated["errors"],
): boolean => {
  const prev = previous ?? [];
  if (JSON.stringify(prev) === JSON.stringify(current)) return false;
  return current.length > 0 || prev.length > 0;
};
